import { createApi } from '@reduxjs/toolkit/query/react';
import type { PositionCategory } from 'store/admins/types';
import API_ROUTES from 'services/apiRoutes';
import { baseQueryWithAuth } from 'services/baseQueryWithAuth';

export type RecipientAdminStatus = 'ACTIVE' | 'CLOSED';

export interface RecipientAdminRow {
  admin_user_id: string;
  first_name: string;
  last_name: string;
  email: string;
  // Not PositionCategory: the super-admin form writes free text here when a staffer
  // picks "Other", so stored values are not confined to the enum the add form offers.
  position_category: string;
  tos_accepted: string | null;
  status: RecipientAdminStatus;
  created_at: string;
  is_primary_contact: boolean;
}

export interface RecipientAdminsResponse {
  admins: RecipientAdminRow[];
  active_count: number;
  archived_count: number;
  active_admin_limit: number;
}

export interface AddRecipientAdminPayload {
  first_name: string;
  last_name: string;
  email: string;
  phone_number?: string | null;
  position_category: PositionCategory;
}

export const recipientAdminsApi = createApi({
  reducerPath: 'recipientAdminsApi',
  baseQuery: baseQueryWithAuth,
  tagTypes: ['RecipientAdmins'],
  endpoints: builder => ({
    getRecipientAdmins: builder.query<RecipientAdminsResponse, { recipientId: string }>({
      query: ({ recipientId }) => API_ROUTES.GET_RECIPIENT_ADMINS(recipientId),
      providesTags: (result, error, { recipientId }) => [
        { type: 'RecipientAdmins' as const, id: recipientId },
      ],
    }),

    addRecipientAdmin: builder.mutation<
      RecipientAdminRow,
      { recipientId: string; body: AddRecipientAdminPayload }
    >({
      query: ({ recipientId, body }) => ({
        url: API_ROUTES.POST_RECIPIENT_ADMIN(recipientId),
        method: 'POST',
        body,
      }),
      invalidatesTags: (result, error, { recipientId }) => [
        { type: 'RecipientAdmins' as const, id: recipientId },
      ],
    }),

    archiveRecipientAdmin: builder.mutation<
      RecipientAdminRow,
      { recipientId: string; adminUserId: string }
    >({
      query: ({ recipientId, adminUserId }) => ({
        url: API_ROUTES.POST_ARCHIVE_RECIPIENT_ADMIN(recipientId, adminUserId),
        method: 'POST',
      }),
      invalidatesTags: (result, error, { recipientId }) => [
        { type: 'RecipientAdmins' as const, id: recipientId },
      ],
    }),

    unarchiveRecipientAdmin: builder.mutation<
      RecipientAdminRow,
      { recipientId: string; adminUserId: string }
    >({
      query: ({ recipientId, adminUserId }) => ({
        url: API_ROUTES.POST_UNARCHIVE_RECIPIENT_ADMIN(recipientId, adminUserId),
        method: 'POST',
      }),
      invalidatesTags: (result, error, { recipientId }) => [
        { type: 'RecipientAdmins' as const, id: recipientId },
      ],
    }),
  }),
});

export const {
  useGetRecipientAdminsQuery,
  useAddRecipientAdminMutation,
  useArchiveRecipientAdminMutation,
  useUnarchiveRecipientAdminMutation,
} = recipientAdminsApi;
